import { apiClient } from '../../../shared/api/axios-instance'
import type { Product } from '../types/product.types'

export interface CreateProductData {
    name: string
    description: string
    sku: string
    category_id: string
    brand?: string
    default_unit_id?: number
    unit_of_measure?: string
    specifications?: Array<{key: string, value: string}>
    status?: 'active' | 'inactive'
    images?: string[]
    warehouses?: any[]
}

export interface UpdateProductData {
    name?: string
    description?: string
    sku?: string
    category_id?: string
    brand?: string
    default_unit_id?: number
    unit_of_measure?: string
    specifications?: Array<{key: string, value: string}>
    status?: 'active' | 'inactive'
    images?: string[]
    warehouses?: any[]
}

const mapProduct = (item: any): Product => {
    const rawImages = item.images || []
    const processedImages = rawImages.map((img: any) => typeof img === 'string' ? img : (img.uuid || img))
    const isUuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(val)
    const primaryUuid = item.image || processedImages.find((img: any) => typeof img === 'string' && isUuid(img)) || processedImages[0]

    return {
        id: String(item.id),
        name: item.name,
        sku: item.sku,
        category: item.category?.name || 'Uncategorized',
        category_id: String(item.category_id),
        brand: item.brands?.[0]?.name || '',
        unit: item.default_unit?.name || '',
        default_unit_id: item.default_unit_id,
        specifications: item.specifications || [],
        price: parseFloat(item.base_price || 0),
        stock: item.stock_quantity || 0,
        status: item.is_active ? 'active' : 'inactive',
        images: processedImages,
        imageId: primaryUuid,
        imageUrl: item.image_url,
        description: item.description,
        warehouses: item.warehouses || [],
        productAttributes: item.product_attributes || []
    }
}

const productService = {
    /**
     * Get all products
     */
    async getProducts(params?: any): Promise<Product[]> {
        const response = await apiClient.get('admin/products', { params })
        return response.data.data.map(mapProduct)
    },

    /**
     * Get product details
     */
    async getProduct(id: string): Promise<Product> {
        const response = await apiClient.get(`admin/products/${id}`)
        return mapProduct(response.data.data)
    },

    /**
     * Create a new product
     */
    async createProduct(data: CreateProductData): Promise<Product> {
        const response = await apiClient.post('admin/products', data)
        return mapProduct(response.data.data)
    },

    /**
     * Update a product
     */
    async updateProduct(id: string, data: UpdateProductData): Promise<Product> {
        const response = await apiClient.put(`admin/products/${id}`, data)
        return mapProduct(response.data.data)
    },

    /**
     * Delete a product
     */
    async deleteProduct(id: string): Promise<void> {
        await apiClient.delete(`admin/products/${id}`)
    },
}

export default productService
